import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { httpGet, httpPost } from "../../services/httpService";
import {
  getMarketPlaceAppsUrl,
  createCompanyMarketAppUrl,
  deleteCompanyMarketAppUrl,
  getCompanyMarketAppsUrl,
  createAccountUrl,
  createAccountLinkUrl,
  getAccountRegisteredUrl,
  getCompaniesUrl,
  getCompanyMarketAppsByCompanyIdUrl,
} from "../../apiUrls/apiUrls";
import { logError } from "../../services/logService";

const initialState = {
  marketplaceAppsData: [],
  isMarketAppsLoading: false,
  createCompanyMarketApp: null,
  isCreateCompanyMarketAppLoading: false,
  deleteCompanyMarketApp: null,
  isDeleteCompanyMarketAppLoading: false,
  companyMarketplaceAppsData: [],
  isCompanyMarketplaceAppsLoading: false,
  createAccount: null,
  isCreateAccountLoading: false,
  recreateLink: null,
  isRecreateLinkLoading: false,
  validateAccountRegistered: [],
  isValidateAccountRegisteredLoading: false,
  companies: [],
  isCompaniesLoading: false,
  companyMarketplaceAppsByCompanyIdData: [],
  isCompanyMarketplaceAppsByCompanyIdLoading: false,
  MarketAppsBaseLoader: false
};

export const getMarketPlaceApps = createAsyncThunk(
  "marketApp/getMarketPlaceApps",
  async (thunkAPI) => {
    const urlToGet = getMarketPlaceAppsUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const createCompanyMarketAppData = createAsyncThunk(
  "marketApp/createCompanyMarketAppData",
  async (data, thunkAPI) => {
    const respData = await httpPost(createCompanyMarketAppUrl(), data);
    return respData;
  },
);

export const deleteCompanyMarketAppData = createAsyncThunk(
  "marketApp/deleteCompanyMarketAppData",
  async (data, thunkAPI) => {
    const respData = await httpPost(deleteCompanyMarketAppUrl(), data);
    return respData;
  },
);

export const getCompanyMarketplaceAppsData = createAsyncThunk(
  "marketApp/getCompanyMarketplaceAppsData",
  async (companyID, thunkAPI) => {
    const respData = await httpGet(getCompanyMarketAppsUrl(companyID));
    return respData;
  },
);

export const createAccountData = createAsyncThunk(
  "pharmacyService/createAccountData",
  async (data, thunkAPI) => {
    const respData = await httpPost(createAccountUrl(), data);
    return respData;
  },
);

export const recreateLinkData = createAsyncThunk(
  "pharmacyService/recreateLinkData",
  async (data, thunkAPI) => {
    const respData = await httpPost(createAccountLinkUrl(), data);
    return respData;
  },
);

export const validateAccountRegisteredData = createAsyncThunk(
  "pharmacyService/validateAccountRegisteredData",
  async (email, thunkApi) => {
    const urlToGet = getAccountRegisteredUrl(email);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getCompaniesData = createAsyncThunk(
  "company/getCompaniesData",
  async ({ searchType, keyword }, thunkApi) => {
    const urlToGet = getCompaniesUrl(searchType, keyword);
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getCompanyMarketplaceAppsByCompanyIdData = createAsyncThunk(
  "marketApp/getCompanyMarketplaceAppsByCompanyIdData",
  async (companyID, thunkAPI) => {
    const respData = await httpGet(
      getCompanyMarketAppsByCompanyIdUrl(companyID),
    );
    return respData;
  },
);

export const marketAppSlice = createSlice({
  name: "marketApp",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getMarketPlaceApps.pending, (state) => {
        state.isMarketAppsLoading = true;
        state.MarketAppsBaseLoader = true;
      })
      .addCase(getMarketPlaceApps.fulfilled, (state, action) => {
        state.isMarketAppsLoading = false;
        state.marketplaceAppsData = action?.payload;
      })
      .addCase(getMarketPlaceApps.rejected, (state, action) => {
        state.isMarketAppsLoading = false;
        state.marketplaceAppsData = [];
      })
      .addCase(createCompanyMarketAppData.pending, (state) => {
        state.isCreateCompanyMarketAppLoading = true;
      })
      .addCase(createCompanyMarketAppData.fulfilled, (state, action) => {
        state.isCreateCompanyMarketAppLoading = false;
        state.createCompanyMarketApp = action?.payload;
      })
      .addCase(createCompanyMarketAppData.rejected, (state, action) => {
        state.isCreateCompanyMarketAppLoading = false;
        state.createCompanyMarketApp = null;
      })
      .addCase(deleteCompanyMarketAppData.pending, (state) => {
        state.isDeleteCompanyMarketAppLoading = true;
      })
      .addCase(deleteCompanyMarketAppData.fulfilled, (state, action) => {
        state.isDeleteCompanyMarketAppLoading = false;
        state.deleteCompanyMarketApp = action?.payload;
      })
      .addCase(deleteCompanyMarketAppData.rejected, (state, action) => {
        state.isDeleteCompanyMarketAppLoading = false;
        state.deleteCompanyMarketApp = null;
      })
      .addCase(getCompanyMarketplaceAppsData.pending, (state) => {
        state.isCompanyMarketplaceAppsLoading = true;
      })
      .addCase(getCompanyMarketplaceAppsData.fulfilled, (state, action) => {
        state.isCompanyMarketplaceAppsLoading = false;
        state.MarketAppsBaseLoader = false;
        state.companyMarketplaceAppsData = action?.payload;
      })
      .addCase(getCompanyMarketplaceAppsData.rejected, (state, action) => {
        state.isCompanyMarketplaceAppsLoading = false;
        state.MarketAppsBaseLoader = false;
        state.companyMarketplaceAppsData = [];
      })
      .addCase(createAccountData.pending, (state) => {
        state.isCreateAccountLoading = true;
      })
      .addCase(createAccountData.fulfilled, (state, action) => {
        state.isCreateAccountLoading = false;
        state.createAccount = action?.payload || null;
      })
      .addCase(createAccountData.rejected, (state, action) => {
        state.isCreateAccountLoading = false;
        logError(action?.error);
      })
      .addCase(recreateLinkData.pending, (state) => {
        state.isRecreateLinkLoading = true;
      })
      .addCase(recreateLinkData.fulfilled, (state, action) => {
        state.isRecreateLinkLoading = false;
        state.recreateAccountLink = action?.payload || null;
      })
      .addCase(recreateLinkData.rejected, (state, action) => {
        state.isRecreateLinkLoading = false;
        logError(action?.error);
      })
      .addCase(validateAccountRegisteredData.pending, (state) => {
        state.isValidateAccountRegisteredLoading = true;
      })
      .addCase(validateAccountRegisteredData.fulfilled, (state, action) => {
        state.isValidateAccountRegisteredLoading = false;
        state.validateAccountRegistered = action?.payload || null;
      })
      .addCase(validateAccountRegisteredData.rejected, (state, action) => {
        state.isValidateAccountRegisteredLoading = false;
        logError(action?.error);
      })
      .addCase(getCompaniesData.pending, (state) => {
        state.isCompaniesLoading = true;
      })
      .addCase(getCompaniesData.fulfilled, (state, action) => {
        state.isCompaniesLoading = false;
        state.companies = action?.payload || [];
      })
      .addCase(getCompaniesData.rejected, (state, action) => {
        state.isCompaniesLoading = false;
        state.companies = [];
      })
      .addCase(getCompanyMarketplaceAppsByCompanyIdData.pending, (state) => {
        state.isCompanyMarketplaceAppsByCompanyIdLoading = true;
      })
      .addCase(
        getCompanyMarketplaceAppsByCompanyIdData.fulfilled,
        (state, action) => {
          state.isCompanyMarketplaceAppsByCompanyIdLoading = false;
          state.companyMarketplaceAppsByCompanyIdData = action?.payload;
        },
      )
      .addCase(
        getCompanyMarketplaceAppsByCompanyIdData.rejected,
        (state, action) => {
          state.isCompanyMarketplaceAppsByCompanyIdLoading = false;
          state.companyMarketplaceAppsByCompanyIdData = [];
        },
      );
  },
});

export default marketAppSlice.reducer;
